import { FieldPath, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import DeleteIcon from "src/core/assets/images/icons/delete.svg?react";
import { FormInlineInput } from "src/core/components/custom-ui/form-inline-input";
import { DocumentCard } from "src/core/components/documents/DocumentCard";
import { Checkbox } from "src/core/components/ui/checkbox";
import { FormMessage } from "src/core/components/ui/form";
import { UploadButton } from "src/core/components/ui/upload-button";
import { useDocumentUploadFieldset } from "src/core/hooks/folder/useDocumentUploadFieldset";
import { cn } from "src/core/lib/utils";
import { Document } from "src/core/types/document";

export type DocumentsFormValues = {
  documents: Record<string, Document>;
};

type RequiredDocument = {
  code: string;
  label: string;
};

export const DocumentsUploadFieldsetEC = ({
  requiredDocuments,
  disabled = false,
  incompleteFields,
  className = "folder-entreprise-documents",
}: {
  requiredDocuments: RequiredDocument[];
  disabled?: boolean;
  incompleteFields?: Set<string>;
  className?: string;
}) => {
  const { t } = useTranslation();
  const { form, deleteDoc, handleFileChange, fileErrors, handlePreviewDocument } =
    useDocumentUploadFieldset<DocumentsFormValues>(requiredDocuments);

  return (
    <div className={className}>
      {requiredDocuments.map((doc) => {
        const isError = form.formState.errors?.documents?.[doc.code];
        const isIncomplete = incompleteFields?.has(doc.code);

        // On a déjà un fichier d'uploadé.
        const hasExistingFile = form.watch(`documents.${doc.code}.file`);

        // On vient d'uploader un fichier via le formulaire.
        const hasFileName = form.watch(`documents.${doc.code}.fileName`);

        const hasFile = !!hasExistingFile || !!hasFileName;
        const fileName = hasExistingFile
          ? form.watch(`documents.${doc.code}.file`)?.name || ""
          : form.watch(`documents.${doc.code}.fileName`) || "";
        const documentId = form.watch(`documents.${doc.code}.fileId`);

        return (
          <div key={doc.code}>
            <div className="flex w-full rounded-lg border border-border">
              <div className="flex-1 basis-[90%] min-w-0">
                <fieldset
                  className={cn(
                    "m-0 border-0 min-h-[50px] min-w-0 relative",
                    isError && "border-alert",
                    isIncomplete && "border-2 border-dashed border-orange-medium rounded-lg"
                  )}
                >
                  <legend className="absolute -top-3 left-10 px-2 font-light bg-dialog">
                    {doc.label}
                  </legend>
                  <div className="flex flex-row items-center min-w-0">
                    <div className="w-full p-10 min-w-0 overflow-hidden">
                      {hasFile ? (
                        <UploadDocumentAction
                          disabled={disabled}
                          form={form}
                          document={{
                            id: documentId,
                            name: fileName,
                            code: doc.code,
                            expirationDate: form.watch(`documents.${doc.code}.expirationDate`),
                          }}
                          handlePreview={handlePreviewDocument}
                        />
                      ) : (
                        <UploadDocumentForm
                          disabled={disabled}
                          fileErrors={fileErrors}
                          form={form}
                          doc={doc}
                          onChange={(e) => handleFileChange(e, doc.code)}
                          expirationDateName={`documents.${doc.code}.expirationDate`}
                        />
                      )}
                    </div>

                    <DeleteDocumentAction
                      hasFile={hasFile}
                      disabled={disabled}
                      onDelete={() =>
                        deleteDoc?.(requiredDocuments.findIndex((d) => d.code === doc.code))
                      }
                    />
                  </div>
                </fieldset>
              </div>
            </div>

            {isIncomplete && (
              <p className="font-light text-sm text-muted">
                {t("global.form.warning.softRequired")}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
};

const UploadDocumentAction = ({
  document,
  disabled,
  form,
  handlePreview,
}: {
  disabled: boolean;
  document: {
    id: string | undefined;
    name: string;
    code: string;
    expirationDate: string | undefined;
  };
  form: UseFormReturn<DocumentsFormValues>;
  handlePreview?: (document: { id: string; name: string }) => void;
}) => {
  const { t } = useTranslation();

  const shouldShowExpirationDate = document.code === "kbis" || document.code === "id";

  return (
    <div className="min-w-0 w-full overflow-hidden">
      <DocumentCard filename={document.name} handlePreview={handlePreview} document={document} />

      {shouldShowExpirationDate && (
        <div className="mt-10 ">
          <FormInlineInput
            type="date"
            name={`documents.${document.code}.expirationDate` as FieldPath<DocumentsFormValues>}
            label={t("folder.documents.expirationDate")}
            wrapperClassName="flex items-center w-full gap-x-30"
            className="w-[160px]"
            disabled={disabled}
            control={form.control}
          />
        </div>
      )}
    </div>
  );
};

export const UploadDocumentForm = ({
  disabled,
  fileErrors,
  form,
  doc,
  onChange,
  expirationDateName,
}: {
  disabled: boolean;
  fileErrors: Record<string, string>;
  form: UseFormReturn<DocumentsFormValues>;
  doc: RequiredDocument;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  expirationDateName: string;
}) => {
  const { t } = useTranslation();
  const isExistingChecked = form.watch(`documents.${doc.code}.existing`);
  const shouldShowExpirationDate = doc.code === "kbis" || doc.code === "id";

  return (
    <div className="flex flex-col gap-x-10 gap-y-5 items-start">
      <div className="flex flex-col items-start justify-between w-full">
        <div className="flex flex-row items-center gap-x-15">
          <UploadButton
            disabled={disabled}
            isExistingChecked={isExistingChecked}
            onChange={onChange}
            doc={doc}
            form={form}
            fileErrors={fileErrors}
            label={t("folder.documents.attach")}
          />

          <div className="flex flex-row items-center gap-x-10">
            <Checkbox
              {...form.register(`documents.${doc.code}.existing`)}
              id={`documents.${doc.code}.existing`}
              onCheckedChange={(checked) => {
                form.setValue(`documents.${doc.code}.existing`, Boolean(checked));
                if (checked) {
                  form.setValue(`documents.${doc.code}.file`, null);
                } else {
                  form.setValue(`documents.${doc.code}`, {
                    file: null,
                    existing: false,
                    fileId: undefined,
                    fileName: undefined,
                    expirationDate: undefined,
                  });
                }
              }}
              checked={Boolean(isExistingChecked)}
              disabled={disabled}
            />
            <label htmlFor={`documents.${doc.code}.existing`} className="text-xs/4 font-light">
              {t("folder.documents.alreadyInGED")}
            </label>
          </div>
        </div>
        {fileErrors[doc.code] && (
          <FormMessage className="self-start w-full text-left">{fileErrors[doc.code]}</FormMessage>
        )}
        {form.formState.errors?.documents?.[doc.code] && (
          <FormMessage className="self-start w-full text-left">
            {form.formState.errors?.documents?.[doc.code]?.message}
          </FormMessage>
        )}
      </div>

      {!isExistingChecked && shouldShowExpirationDate && (
        <div className="w-full">
          <FormInlineInput
            type="date"
            name={expirationDateName as FieldPath<DocumentsFormValues>}
            label={t("folder.documents.expirationDate")}
            wrapperClassName="flex items-center w-full gap-x-30"
            className="w-[160px]"
            disabled={disabled}
            control={form.control}
          />
        </div>
      )}
    </div>
  );
};

const DeleteDocumentAction = ({
  hasFile,
  disabled,
  onDelete,
}: {
  hasFile: boolean;
  disabled: boolean;
  onDelete: () => void;
}) => {
  if (!hasFile || disabled) {
    return null;
  }

  return (
    <button
      type="button"
      className="flex items-center justify-center basis-[10%] self-stretch border-l rounded-e-lg border-border bg-white"
      onClick={onDelete}
    >
      <div className="flex items-center justify-center">
        <DeleteIcon className="cursor-pointer shrink-0" />
      </div>
    </button>
  );
};
